import React, { useEffect, useRef, useState } from 'react';
import { getServiceConfig } from '@cloistr/collab-common/config';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Header } from '@cloistr/ui';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Key, Fingerprint } from 'lucide-react';
import toast from 'react-hot-toast';

interface LoginForm {
  email: string;
  password: string;
}

interface LightningChallenge {
  k1: string;
  lnurl: string;
  expiresAt: string;
}

/**
 * The server's discoverable-login challenge lives five minutes. Renew the
 * autofill request a minute before that, so a passkey picked from autofill
 * always answers a live challenge.
 */
const PASSKEY_AUTOFILL_RENEW_MS = 4 * 60 * 1000;

export default function Login() {
  const { login, loginWithNostr, getLightningChallenge, loginWithLightning, loginWithWebAuthn, loginWithWebAuthnDiscoverable, startPasskeyAutofill, isWebAuthnAvailable, loading } = useAuth();
  const [authMethod, setAuthMethod] = useState<'email' | 'nostr' | 'lightning' | 'passkey'>('email');
  const [lightningAddress, setLightningAddress] = useState('');
  const [lightningChallenge, setLightningChallenge] = useState<LightningChallenge | null>(null);
  const [lightningSignature, setLightningSignature] = useState('');
  const [lightningLinkingKey, setLightningLinkingKey] = useState('');
  const [passkeyEmail, setPasskeyEmail] = useState('');
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  // Passkey autofill: while this page is open, one conditional WebAuthn
  // request stays pending so the browser offers saved passkeys in the email
  // field's autofill. Only one WebAuthn request can be pending, so the passkey
  // buttons cancel it first and re-arm it when they finish.
  const autofill = useRef<AbortController | null>(null);
  const leaving = useRef(false);
  const startAutofillRef = useRef(startPasskeyAutofill);
  startAutofillRef.current = startPasskeyAutofill;

  const armPasskeyAutofill = () => {
    if (leaving.current) return;
    autofill.current?.abort('replaced');
    const ac = new AbortController();
    autofill.current = ac;
    const renew = setTimeout(() => ac.abort('renew'), PASSKEY_AUTOFILL_RENEW_MS);
    startAutofillRef.current(ac.signal).then((outcome) => {
      clearTimeout(renew);
      // Re-arm only when the challenge was renewed. A failure stops here
      // rather than retrying in a loop against the auth rate limit.
      if (outcome === 'aborted' && ac.signal.reason === 'renew') armPasskeyAutofill();
    });
  };

  const pausePasskeyAutofill = () => {
    autofill.current?.abort('paused');
    autofill.current = null;
  };

  useEffect(() => {
    if (!isWebAuthnAvailable) return;
    leaving.current = false;
    armPasskeyAutofill();
    return () => {
      leaving.current = true;
      pausePasskeyAutofill();
    };
  }, [isWebAuthnAvailable]);

  /**
   * Runs an explicit passkey sign-in with autofill paused. Re-arms only if it
   * fails: on success the page is about to leave, and a fresh request would
   * spend a rate-limited challenge for nothing.
   */
  const withAutofillPaused = async (run: () => Promise<void>) => {
    pausePasskeyAutofill();
    try {
      await run();
    } catch (error) {
      armPasskeyAutofill();
      throw error;
    }
  };

  const onEmailSubmit = async (data: LoginForm) => {
    try {
      await login(data.email, data.password);
    } catch (error) {
      // Error handling is done in AuthContext
    }
  };

  const onNostrLogin = async () => {
    try {
      // PublicRoute leaves /login once the session is set.
      await loginWithNostr();
    } catch {
      // AuthContext shows the error
    }
  };

  const onLightningGetChallenge = async () => {
    if (!lightningAddress || !lightningAddress.includes('@')) {
      toast.error('Please enter a valid Lightning address (e.g., user@wallet.com)');
      return;
    }

    try {
      const challenge = await getLightningChallenge(lightningAddress);
      setLightningChallenge(challenge);
      toast.success('Challenge received! Sign with your Lightning wallet');
    } catch (error) {
      // Error handling is done in AuthContext
    }
  };

  const onLightningLogin = async () => {
    if (!lightningChallenge) {
      toast.error('Please get a challenge first');
      return;
    }

    if (!lightningSignature || lightningSignature.length !== 128) {
      toast.error('Please enter a valid 128-character signature');
      return;
    }

    if (!lightningLinkingKey || lightningLinkingKey.length !== 66) {
      toast.error('Please enter a valid 66-character linking key');
      return;
    }

    try {
      await loginWithLightning(lightningChallenge.k1, lightningSignature, lightningLinkingKey);
    } catch (error) {
      // Error handling is done in AuthContext
    }
  };

  const onPasskeyLogin = async () => {
    if (!passkeyEmail || !passkeyEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      await withAutofillPaused(() => loginWithWebAuthn(passkeyEmail));
    } catch (error) {
      // Error handling is done in AuthContext
    }
  };

  const onPasskeyDiscoverableLogin = async () => {
    try {
      await withAutofillPaused(loginWithWebAuthnDiscoverable);
    } catch (error) {
      // Error handling is done in AuthContext
    }
  };

  return (
    <div className="min-h-dvh flex flex-col bg-cloistr-bg">
      <Header
        activeServiceId="vault"
        auth={{ authenticated: false }}
        signerUrl={getServiceConfig().signerUrl}
      />
      <div className="flex-1 flex items-center justify-center">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="card">
          <div className="card-header text-center">
            <div className="mx-auto h-12 w-12 mb-4">
              <img src="/cloistr-icon.svg" alt="Cloistr" className="h-12 w-12" />
            </div>
            <h2 className="card-title">Welcome to Cloistr Vault</h2>
            <p className="card-description">
              Your zero-knowledge password manager. Secrets are encrypted on your
              device before they ever leave it — not even we can read them. Sign
              in with your Cloistr identity, or any method below.
            </p>
          </div>

          <div className="card-content space-y-6">
            {/* Auth Method Toggle */}
            <div className="grid grid-cols-2 gap-1 bg-cloistr-bg-hover rounded-lg p-1">
              <button
                type="button"
                className={`py-2 px-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center ${
                  authMethod === 'email'
                    ? 'bg-cloistr-bg-elevated text-cloistr-primary-text shadow-sm'
                    : 'text-cloistr-text-muted hover:text-cloistr-text'
                }`}
                onClick={() => setAuthMethod('email')}
              >
                <Mail className="w-4 h-4 mr-1" />
                Email
              </button>
              {isWebAuthnAvailable && (
                <button
                  type="button"
                  className={`py-2 px-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center ${
                    authMethod === 'passkey'
                      ? 'bg-cloistr-bg-elevated text-cloistr-primary-text shadow-sm'
                      : 'text-cloistr-text-muted hover:text-cloistr-text'
                  }`}
                  onClick={() => setAuthMethod('passkey')}
                >
                  <Fingerprint className="w-4 h-4 mr-1" />
                  Passkey
                </button>
              )}
              <button
                type="button"
                className={`py-2 px-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center ${
                  authMethod === 'nostr'
                    ? 'bg-cloistr-bg-elevated text-cloistr-primary-text shadow-sm'
                    : 'text-cloistr-text-muted hover:text-cloistr-text'
                }`}
                onClick={() => setAuthMethod('nostr')}
              >
                <Key className="w-4 h-4 mr-1" />
                Nostr
              </button>
              {/* Lightning login is disabled server-side until it is rebuilt on LUD-04. */}
            </div>

            {authMethod === 'email' && (
              <form onSubmit={handleSubmit(onEmailSubmit)} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-cloistr-text mb-2">
                    Email address
                  </label>
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: 'Please enter a valid email',
                      },
                    })}
                    type="email"
                    autoComplete="username webauthn"
                    className="input w-full"
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-cloistr-error">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-cloistr-text mb-2">
                    Master password
                  </label>
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    })}
                    type="password"
                    className="input w-full"
                    placeholder="Enter your master password"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-cloistr-error">{errors.password.message}</p>
                  )}
                  <p className="mt-1 text-xs text-cloistr-text-muted">
                    Your master password never leaves this device. You sign in with a key derived from it.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
            )}

            {authMethod === 'passkey' && (
              <div className="space-y-4">
                <div className="text-center">
                  <Fingerprint className="w-12 h-12 mx-auto text-cloistr-primary-text mb-2" />
                  <p className="text-sm text-cloistr-text-muted">
                    Sign in with your device's built-in authenticator
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onPasskeyDiscoverableLogin}
                  disabled={loading}
                  className="btn-primary w-full"
                >
                  {loading ? 'Authenticating...' : 'Sign in with Passkey'}
                </button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-cloistr-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-cloistr-bg-elevated px-2 text-cloistr-text-muted">or enter email</span>
                  </div>
                </div>

                <div>
                  <label htmlFor="passkeyEmail" className="block text-sm font-medium text-cloistr-text mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={passkeyEmail}
                    onChange={(e) => setPasskeyEmail(e.target.value)}
                    autoComplete="username webauthn"
                    className="input w-full"
                    placeholder="Enter your email"
                  />
                </div>

                <button
                  type="button"
                  onClick={onPasskeyLogin}
                  disabled={loading || !passkeyEmail}
                  className="btn-secondary w-full"
                >
                  {loading ? 'Authenticating...' : 'Sign in with Email + Passkey'}
                </button>

                <div className="bg-cloistr-bg-hover/50 p-3 rounded-lg">
                  <p className="text-xs text-cloistr-text-muted">
                    <strong>Note:</strong> Passkeys use Face ID, Touch ID, Windows Hello, or hardware security keys
                    for passwordless authentication.
                  </p>
                </div>
              </div>
            )}

            {authMethod === 'nostr' && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={onNostrLogin}
                  disabled={loading}
                  className="btn-primary w-full"
                >
                  {loading ? 'Signing in...' : 'Sign in with Nostr extension'}
                </button>

                <div className="bg-cloistr-bg-hover/50 p-3 rounded-lg">
                  <p className="text-xs text-cloistr-text-muted">
                    Your browser extension (NIP-07) signs a one-time challenge. Your key never leaves it.
                  </p>
                </div>
              </div>
            )}

            {authMethod === 'lightning' && (
              <div className="space-y-4">
                {!lightningChallenge ? (
                  <>
                    <div>
                      <label htmlFor="lightningAddress" className="block text-sm font-medium text-cloistr-text mb-2">
                        Lightning Address
                      </label>
                      <input
                        type="text"
                        value={lightningAddress}
                        onChange={(e) => setLightningAddress(e.target.value)}
                        className="input w-full"
                        placeholder="user@wallet.com"
                      />
                      <p className="mt-1 text-xs text-cloistr-text-muted">
                        Enter your Lightning address (e.g., alice@getalby.com)
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={onLightningGetChallenge}
                      disabled={loading || !lightningAddress}
                      className="btn-primary w-full"
                    >
                      {loading ? 'Getting challenge...' : 'Get Challenge'}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="bg-cloistr-bg-hover/50 p-3 rounded-lg">
                      <p className="text-xs text-cloistr-text-muted mb-2">
                        <strong>Challenge (k1):</strong>
                      </p>
                      <code className="text-xs break-all">{lightningChallenge.k1}</code>
                    </div>

                    <div>
                      <label htmlFor="lightningSignature" className="block text-sm font-medium text-cloistr-text mb-2">
                        Signature
                      </label>
                      <input
                        type="text"
                        value={lightningSignature}
                        onChange={(e) => setLightningSignature(e.target.value)}
                        className="input w-full font-mono text-sm"
                        placeholder="128-character DER signature"
                        maxLength={128}
                      />
                    </div>

                    <div>
                      <label htmlFor="lightningLinkingKey" className="block text-sm font-medium text-cloistr-text mb-2">
                        Linking Key
                      </label>
                      <input
                        type="text"
                        value={lightningLinkingKey}
                        onChange={(e) => setLightningLinkingKey(e.target.value)}
                        className="input w-full font-mono text-sm"
                        placeholder="66-character compressed public key"
                        maxLength={66}
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setLightningChallenge(null);
                          setLightningSignature('');
                          setLightningLinkingKey('');
                        }}
                        className="btn-secondary flex-1"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={onLightningLogin}
                        disabled={loading || !lightningSignature || !lightningLinkingKey}
                        className="btn-primary flex-1"
                      >
                        {loading ? 'Signing in...' : 'Sign in'}
                      </button>
                    </div>
                  </>
                )}

                <div className="bg-cloistr-bg-hover/50 p-3 rounded-lg">
                  <p className="text-xs text-cloistr-text-muted">
                    <strong>Note:</strong> Lightning authentication uses LNURL-auth. Sign the challenge with your
                    Lightning wallet and paste the signature and linking key above.
                  </p>
                </div>
              </div>
            )}

            <div className="text-center">
              <p className="text-sm text-cloistr-text-muted">
                Don't have an account?{' '}
                <Link to="/register" className="text-cloistr-primary-text hover:underline">
                  Create one here
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-xs text-cloistr-text-muted">
            Your data is encrypted locally and never stored unencrypted on our servers
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}